import React, { useState, useEffect } from 'react';
import { donationService } from '../../services/firebaseService';
import { DonationContent, DonationOption } from '../../types';

interface PaymentService {
  id: string;
  name: string;
  type: DonationOption['type'];
  icon: string;
  iconColor: string;
  placeholder: string;
  description: string;
  validation?: (url: string) => string | null;
}

const DonationManager: React.FC = () => {
  const [donationContent, setDonationContent] = useState<DonationContent>({
    title: 'Donate',
    description: 'Choose a donation method below.',
    options: []
  });
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [savedField, setSavedField] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const paymentServices: PaymentService[] = [
    {
      id: 'stripe',
      name: 'Stripe',
      type: 'stripe',
      icon: '💳',
      iconColor: '#635BFF',
      placeholder: 'https://buy.stripe.com/... or https://checkout.stripe.com/...',
      description: 'Primary payment processor - supports cards, Apple Pay, Google Pay automatically',
      validation: (url: string) => {
        if (url && !url.includes('stripe.com/') && !url.includes('buy.stripe.com/')) {
          return 'Please enter a valid Stripe payment link';
        }
        return null;
      }
    },
    {
      id: 'paypal',
      name: 'PayPal',
      type: 'paypal',
      icon: 'P',
      iconColor: '#0070BA',
      placeholder: 'https://paypal.me/yourname',
      description: 'PayPal.me link for direct payments',
      validation: (url: string) => {
        if (url && !url.includes('paypal.me/') && !url.includes('paypal.com/')) {
          return 'Please enter a valid PayPal URL';
        }
        return null;
      }
    },
    {
      id: 'venmo',
      name: 'Venmo',
      type: 'venmo',
      icon: '$',
      iconColor: '#3D95CE',
      placeholder: '@yourvenmoname',
      description: 'Your Venmo username (include @ symbol)',
      validation: (url: string) => {
        if (url && !url.startsWith('@')) {
          return 'Venmo username should start with @';
        }
        return null;
      }
    },
    {
      id: 'zelle',
      name: 'Zelle',
      type: 'zelle',
      icon: '$',
      iconColor: '#6C1D45',
      placeholder: 'your-email@example.com',
      description: 'Email or phone number linked to Zelle'
    },
    {
      id: 'buymeacoffee',
      name: 'Buy Me a Coffee',
      type: 'buymeacoffee',
      icon: '☕',
      iconColor: '#FFDD00',
      placeholder: 'https://www.buymeacoffee.com/yourname',
      description: 'Popular donation platform for creators',
      validation: (url: string) => {
        if (url && !url.includes('buymeacoffee.com/')) {
          return 'Please enter a valid Buy Me a Coffee URL';
        }
        return null;
      }
    },
    {
      id: 'kofi',
      name: 'Ko-fi',
      type: 'kofi',
      icon: '☕',
      iconColor: '#FF5E5B',
      placeholder: 'https://ko-fi.com/yourname',
      description: 'Simple donation platform with no fees',
      validation: (url: string) => {
        if (url && !url.includes('ko-fi.com/')) {
          return 'Please enter a valid Ko-fi URL';
        }
        return null;
      }
    }
  ];

  useEffect(() => {
    loadDonationContent();
  }, []);

  const loadDonationContent = async () => {
    try {
      const content = await donationService.getDonationContent();
      if (content) {
        setDonationContent(content);
      }
    } catch (error) {
      console.error('Error loading donation content:', error);
      setMessage({ type: 'error', text: 'Failed to load donation content' });
    } finally {
      setLoading(false);
    }
  };

  const getOptionForService = (serviceId: string): DonationOption | undefined => {
    return donationContent.options.find(opt => opt.type === serviceId);
  };

  const isConfigured = (serviceId: string): boolean => {
    const option = getOptionForService(serviceId);
    return !!(option?.paymentUrl?.trim());
  };

  const handleSave = async (service: PaymentService) => {
    const option = getOptionForService(service.id);
    const url = option?.paymentUrl?.trim() || '';
    
    // Validate if validation function exists
    if (service.validation && url) {
      const error = service.validation(url);
      if (error) {
        setMessage({ type: 'error', text: error });
        setTimeout(() => setMessage(null), 3000);
        return;
      }
    }
    
    setSavingField(service.id);
    setMessage(null);
    
    try {
      const updatedOptions = [...donationContent.options];
      const existingIndex = updatedOptions.findIndex(opt => opt.type === service.id);
      
      if (url.trim() === '') {
        // Remove option if URL is empty
        if (existingIndex !== -1) {
          updatedOptions.splice(existingIndex, 1);
        }
      } else {
        // Add or update option
        const newOption: DonationOption = {
          id: existingIndex !== -1 ? updatedOptions[existingIndex].id : Date.now().toString(),
          name: service.name,
          description: service.description,
          enabled: true,
          type: service.type,
          paymentUrl: url,
          buttonText: 'Donate Now',
          order: existingIndex !== -1 ? updatedOptions[existingIndex].order : updatedOptions.length
        };
        
        if (existingIndex !== -1) {
          updatedOptions[existingIndex] = newOption;
        } else {
          updatedOptions.push(newOption);
        }
      }
      
      const updatedContent: DonationContent = {
        ...donationContent,
        options: updatedOptions,
        updatedAt: new Date()
      };
      
      await donationService.updateDonationContent(updatedContent);
      setDonationContent(updatedContent);
      
      setSavedField(service.id);
      setTimeout(() => setSavedField(null), 2000);
      setMessage({ type: 'success', text: `${service.name} saved successfully!` });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving donation content:', error);
      setMessage({ type: 'error', text: `Failed to save: ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSavingField(null);
    }
  };

  const handleInputChange = (service: PaymentService, value: string) => {
    const updatedOptions = [...donationContent.options];
    const existingIndex = updatedOptions.findIndex(opt => opt.type === service.id);
    
    if (existingIndex !== -1) {
      updatedOptions[existingIndex] = {
        ...updatedOptions[existingIndex],
        paymentUrl: value
      };
    } else {
      // Create new option if it doesn't exist
      updatedOptions.push({
        id: Date.now().toString(),
        name: service.name,
        description: service.description,
        enabled: true,
        type: service.type,
        paymentUrl: value,
        buttonText: 'Donate Now',
        order: updatedOptions.length
      });
    }
    
    setDonationContent({
      ...donationContent,
      options: updatedOptions
    });
  };

  const handleSaveAll = async () => {
    setSavingField('all');
    setMessage(null);
    try {
      await donationService.updateDonationContent(donationContent);
      setMessage({ type: 'success', text: 'All donation settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving donation content:', error);
      setMessage({ type: 'error', text: `Failed to save: ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSavingField(null);
    }
  };

  const renderPaymentMethod = (service: PaymentService, compact: boolean = false) => {
    const option = getOptionForService(service.id);
    const currentValue = option?.paymentUrl || '';
    const isSaving = savingField === service.id;
    const isSaved = savedField === service.id;
    const configured = isConfigured(service.id);
    
    if (compact) {
      // Compact horizontal card for grid layout
      return (
        <div 
          key={service.id} 
          style={{
            border: `2px solid ${configured ? service.iconColor : '#e0e0e0'}`,
            borderRadius: '10px',
            padding: '16px',
            backgroundColor: 'white',
            boxShadow: configured 
              ? `0 2px 6px rgba(0,0,0,0.08), 0 0 0 1px ${service.iconColor}20`
              : '0 1px 3px rgba(0,0,0,0.05)',
            transition: 'all 0.2s ease',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = configured
              ? `0 4px 10px rgba(0,0,0,0.12), 0 0 0 1px ${service.iconColor}30`
              : '0 2px 6px rgba(0,0,0,0.1)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = configured
              ? `0 2px 6px rgba(0,0,0,0.08), 0 0 0 1px ${service.iconColor}20`
              : '0 1px 3px rgba(0,0,0,0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {/* Status Badge */}
          {configured && (
            <div
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: '#28a745',
                color: 'white',
                fontSize: '10px',
                fontWeight: '600',
                padding: '3px 8px',
                borderRadius: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              ✓
            </div>
          )}
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
            gap: '10px'
          }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: service.iconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: service.iconColor === '#FFDD00' ? '#333' : 'white',
                fontSize: '20px',
                fontWeight: 'bold',
                flexShrink: 0,
                boxShadow: `0 2px 4px ${service.iconColor}40`
              }}
            >
              {service.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                marginBottom: '2px',
                flexWrap: 'wrap'
              }}>
                <h4 style={{ 
                  fontWeight: '700', 
                  fontSize: '16px',
                  margin: 0,
                  color: '#1a1a1a'
                }}>
                  {service.name}
                </h4>
                {service.id === 'stripe' && (
                  <span style={{ 
                    fontSize: '10px', 
                    backgroundColor: '#635BFF',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Rec
                  </span>
                )}
              </div>
              <p style={{ 
                fontSize: '12px', 
                color: '#666',
                margin: 0,
                lineHeight: 1.4
              }}>
                {service.description}
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginTop: 'auto' }}>
            <input
              type="text"
              value={currentValue}
              onChange={(e) => handleInputChange(service, e.target.value)}
              placeholder={service.placeholder}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: '6px',
                border: `2px solid ${configured ? service.iconColor : '#ddd'}`,
                fontSize: '13px',
                transition: 'all 0.2s ease',
                outline: 'none',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = service.iconColor;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${service.iconColor}20`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = configured ? service.iconColor : '#ddd';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            <button
              onClick={() => handleSave(service)}
              disabled={isSaving}
              style={{
                backgroundColor: isSaved ? '#28a745' : (configured ? service.iconColor : '#007aff'),
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 16px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                minWidth: '80px',
                justifyContent: 'center',
                opacity: isSaving ? 0.7 : 1,
                transition: 'all 0.2s ease',
                boxShadow: isSaved 
                  ? '0 2px 4px rgba(40, 167, 69, 0.3)'
                  : '0 2px 4px rgba(0,0,0,0.1)',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (!isSaving) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = isSaved
                    ? '0 4px 8px rgba(40, 167, 69, 0.4)'
                    : '0 4px 8px rgba(0,0,0,0.15)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = isSaved
                  ? '0 2px 4px rgba(40, 167, 69, 0.3)'
                  : '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              {isSaving ? (
                <>
                  <span className="spinner" style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite'
                  }} />
                  ...
                </>
              ) : isSaved ? (
                '✓'
              ) : (
                configured ? 'Update' : 'Save'
              )}
            </button>
          </div>
        </div>
      );
    }
    
    // Full-width card for Stripe
    return (
      <div 
        key={service.id} 
        style={{
          border: `2px solid ${configured ? service.iconColor : '#e0e0e0'}`,
          borderRadius: '12px',
          padding: '20px',
          backgroundColor: 'white',
          boxShadow: configured 
            ? `0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px ${service.iconColor}20`
            : '0 1px 3px rgba(0,0,0,0.05)',
          transition: 'all 0.2s ease',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = configured
            ? `0 4px 12px rgba(0,0,0,0.12), 0 0 0 1px ${service.iconColor}30`
            : '0 2px 6px rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = configured
            ? `0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px ${service.iconColor}20`
            : '0 1px 3px rgba(0,0,0,0.05)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Status Badge */}
        {configured && (
          <div
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              backgroundColor: '#28a745',
              color: 'white',
              fontSize: '11px',
              fontWeight: '600',
              padding: '4px 10px',
              borderRadius: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            ✓ Configured
          </div>
        )}
        
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: '16px',
          gap: '12px'
        }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '10px',
              backgroundColor: service.iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: service.iconColor === '#FFDD00' ? '#333' : 'white',
              fontSize: '24px',
              fontWeight: 'bold',
              flexShrink: 0,
              boxShadow: `0 2px 4px ${service.iconColor}40`
            }}
          >
            {service.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '4px',
              flexWrap: 'wrap'
            }}>
              <h4 style={{ 
                fontWeight: '700', 
                fontSize: '18px',
                margin: 0,
                color: '#1a1a1a'
              }}>
                {service.name}
              </h4>
              {service.id === 'stripe' && (
                <span style={{ 
                  fontSize: '11px', 
                  backgroundColor: '#635BFF',
                  color: 'white',
                  padding: '3px 8px',
                  borderRadius: '10px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Recommended
                </span>
              )}
            </div>
            <p style={{ 
              fontSize: '13px', 
              color: '#666',
              margin: 0,
              lineHeight: 1.5
            }}>
              {service.description}
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <input
            type="text"
            value={currentValue}
            onChange={(e) => handleInputChange(service, e.target.value)}
            placeholder={service.placeholder}
            style={{
              flex: 1,
              padding: '14px 16px',
              borderRadius: '8px',
              border: `2px solid ${configured ? service.iconColor : '#ddd'}`,
              fontSize: '14px',
              transition: 'all 0.2s ease',
              outline: 'none',
              fontFamily: 'inherit'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = service.iconColor;
              e.currentTarget.style.boxShadow = `0 0 0 3px ${service.iconColor}20`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = configured ? service.iconColor : '#ddd';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          <button
            onClick={() => handleSave(service)}
            disabled={isSaving}
            style={{
              backgroundColor: isSaved ? '#28a745' : (configured ? service.iconColor : '#007aff'),
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '14px 20px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '100px',
              justifyContent: 'center',
              opacity: isSaving ? 0.7 : 1,
              transition: 'all 0.2s ease',
              boxShadow: isSaved 
                ? '0 2px 4px rgba(40, 167, 69, 0.3)'
                : '0 2px 4px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = isSaved
                  ? '0 4px 8px rgba(40, 167, 69, 0.4)'
                  : '0 4px 8px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = isSaved
                ? '0 2px 4px rgba(40, 167, 69, 0.3)'
                : '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            {isSaving ? (
              <>
                <span className="spinner" style={{
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite'
                }} />
                Saving...
              </>
            ) : isSaved ? (
              <>
                ✓ Saved!
              </>
            ) : (
              configured ? 'Update' : 'Save'
            )}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <div className="admin-content">
          <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '18px', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  const stripeService = paymentServices.find(s => s.id === 'stripe')!;
  const otherServices = paymentServices.filter(s => s.id !== 'stripe');

  return (
    <div className="admin-layout">
      <div className="admin-content">
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#1a1a1a',
            marginBottom: '8px'
          }}>
            Donation Settings
          </h1>
          <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
            Configure payment methods for your donation page
          </p>
        </div>
        
        {message && (
          <div
            style={{
              padding: '14px 18px',
              marginBottom: '1.5rem',
              borderRadius: '8px',
              backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
              color: message.type === 'success' ? '#155724' : '#721c24',
              border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {message.text}
          </div>
        )}

        {/* Two-Column Layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '350px 1fr',
          gap: '2rem',
          marginBottom: '2.5rem'
        }}>
          {/* Left Column: Page Settings */}
          <div className="card" style={{ 
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: 'fit-content',
            position: 'sticky',
            top: '2rem'
          }}>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '700', 
              marginBottom: '16px',
              color: '#1a1a1a'
            }}>
              Page Settings
            </h3>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                fontSize: '13px',
                color: '#333'
              }}>
                Page Title
              </label>
              <input
                type="text"
                value={donationContent.title || ''}
                onChange={(e) => setDonationContent({ ...donationContent, title: e.target.value })}
                style={{ 
                  width: '100%', 
                  padding: '10px 14px', 
                  fontSize: '14px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#007aff';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 122, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#ddd';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                fontSize: '13px',
                color: '#333'
              }}>
                Page Description
              </label>
              <textarea
                value={donationContent.description || ''}
                onChange={(e) => setDonationContent({ ...donationContent, description: e.target.value })}
                style={{ 
                  width: '100%', 
                  padding: '10px 14px', 
                  fontSize: '14px',
                  minHeight: '90px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#007aff';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 122, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#ddd';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          {/* Right Column: Payment Methods */}
          <div>
            {/* Stripe - Prominently Featured */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ 
                marginBottom: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  margin: 0,
                  color: '#1a1a1a'
                }}>
                  💰 Recommended Payment Method
                </h3>
              </div>
              {renderPaymentMethod(stripeService, false)}
            </div>

            {/* Other Payment Methods */}
            <div>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '700', 
                marginBottom: '16px',
                color: '#1a1a1a'
              }}>
                Additional Payment Methods
              </h3>
              <p style={{ 
                margin: '0 0 20px 0', 
                color: '#666', 
                fontSize: '13px', 
                lineHeight: 1.6 
              }}>
                Configure additional payment methods. Only methods with URLs configured will appear on the public donation page.
              </p>
              
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '16px'
              }}>
                {otherServices.map(service => renderPaymentMethod(service, true))}
              </div>
            </div>
          </div>
        </div>
          
        <div style={{
          marginTop: '32px',
          padding: '16px 20px',
          backgroundColor: '#f0f7ff',
          border: '1px solid #b3d9ff',
          borderRadius: '10px',
          fontSize: '14px',
          color: '#0066cc',
          lineHeight: 1.6
        }}>
          <strong style={{ display: 'block', marginBottom: '4px' }}>💡 Tip:</strong>
          Only services with URLs configured will appear on the public donation page. 
          Leave fields empty if you don't want to offer that payment method. 
          <strong> Stripe is recommended</strong> as it automatically supports multiple payment methods including Apple Pay and Google Pay.
        </div>

        <div style={{ 
          marginTop: '2.5rem',
          paddingTop: '2rem',
          borderTop: '2px solid #e0e0e0'
        }}>
          <button
            onClick={handleSaveAll}
            disabled={savingField === 'all'}
            className="btn btn-primary"
            style={{ 
              fontSize: '16px', 
              padding: '14px 32px',
              opacity: savingField === 'all' ? 0.7 : 1,
              cursor: savingField === 'all' ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              borderRadius: '8px',
              border: 'none',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (savingField !== 'all') {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {savingField === 'all' ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .admin-content > div > div[style*="grid-template-columns: 350px"] {
            grid-template-columns: 1fr !important;
          }
          .admin-content > div > div[style*="grid-template-columns: 350px"] > div[style*="position: sticky"] {
            position: relative !important;
            top: 0 !important;
          }
        }
        @media (max-width: 768px) {
          .admin-content > div > div[style*="grid"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DonationManager;
